import { ComponentProps } from 'react'

import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import {
  PerformanceProcessAgreementStatusAlertEsignaturesFragment,
  PerformanceProcessAgreementStatusAlertFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

interface UseStatusAlertParams {
  agreement: PerformanceProcessAgreementStatusAlertFragment | null
  esignatures: PerformanceProcessAgreementStatusAlertEsignaturesFragment[]
}

// TODO: COPY - Action plan e-signature
export const useStatusAlert = ({
  agreement,
  esignatures,
}: UseStatusAlertParams): ComponentProps<typeof OneAlert> | null => {
  const { access } = useCurrentSession()
  const { isAgreementDocumentEnabled } = useNewPerformanceModuleContext()

  if (!agreement || !isAgreementDocumentEnabled) return null

  if (agreement.status === 'signed') {
    // When the document is being generated
    if (agreement.generatingDocument) return null

    const agreementDocument = agreement.agreementDocuments[0]
    const document = agreementDocument?.document

    // When there's a document but the user cannot see it
    if (agreementDocument && !document) return null

    // When the document should be e-signed
    const signatureStatus = document?.signatureStatus
    if (signatureStatus) {
      if (signatureStatus === 'completed') {
        return {
          title: 'Action plan completed',
          description: 'Action plan was signed by both parties.',
          variant: 'positive',
        }
      }

      if (signatureStatus === 'pending' || signatureStatus === 'partially_signed') {
        return {
          title: 'Action plan ready to e-sign',
          description:
            'Action plan was confirmed by both parties. Now it is ready to start the electronic signature processs.',
          variant: 'info',
        }
      }

      if (signatureStatus === 'declined') {
        const declinedSignee = esignatures?.find(
          (signature) => signature.status === 'declined'
        )?.signee

        if (!declinedSignee) return null

        return {
          title: 'Action plan declined',
          description: `${declinedSignee.firstName} has declined to sign the document. It is no longer legally valid.`,
          variant: 'warning',
        }
      }

      if (signatureStatus === 'expired') {
        return {
          title: 'Action plan expired',
          description:
            'This document can no longer be signed because it has expired. Documents that remain unsigned by either party for more than one year automatically become invalid.',
          variant: 'critical',
        }
      }

      if (document.deletedAt) {
        const date = i18nDateToString(stringToDate(document.deletedAt, 'backend_datetime'), 'ui')

        return {
          title: 'Action plan moved to trash',
          description: `It will remain there for 30 days before being permanently deleted. The document was deleted on ${date}.`,
          variant: 'critical',
        }
      }

      return null
    }

    // When the document should not be e-signed or when it does not even exist
    return {
      title: 'Action plan completed',
      description: 'Action plan was confirmed by both parties.',
      variant: 'positive',
    }
  }

  if (agreement.locked) {
    const firstSignedByMe =
      (agreement.managerSignedAt && agreement.signer?.id == access.id) ||
      (agreement.targetSignedAt && agreement.target?.access?.id == access.id)

    if (firstSignedByMe) {
      return {
        title: 'Action plan confirmed',
        description:
          'You can no longer edit this action plan because you have already confirmed it.',
        variant: 'warning',
      }
    }

    return {
      title: 'Action plan confirmed',
      description: `${
        agreement.signer?.firstName || agreement.target?.access?.firstName
      } has already confirmed the action plan. No further edits are allowed.`,
      variant: 'warning',
    }
  }

  return null
}
